import React, { SetStateAction, Dispatch } from 'react'
import { Form, Button, Tooltip, OverlayTrigger} from 'react-angle-dashboard-components'
import ReactToPrint from 'react-to-print'
import { SmallTitle, FormLabel, FormControl } from '../styles'
import { FormData } from '../../../interfaces/claims'
import { ClaimNumberSearch } from './ClaimNumberSearch'
import { MemberSearch } from './ClaimMemberSearch'
import styled from 'styled-components'
import {
  GetClaimsByMemberReqBody,
  GetClaimsByNumberReqBody
} from '../../../services/claims'

interface SearchFormProps {
  showPrintBtn: boolean
  setNewQuery: (enabled: boolean) => void
  formData: FormData
  hasClaimNumber: boolean
  setHasClaimNumber: Dispatch<SetStateAction<boolean>>

  setClaimNumberQuery: Dispatch<SetStateAction<GetClaimsByNumberReqBody>>
  setClaimMemberQuery: Dispatch<SetStateAction<GetClaimsByMemberReqBody>>
  setFormData: Dispatch<SetStateAction<FormData>>

  printRef: any
  onBeforePrint: () => Promise<unknown>
  onAfterPrint: () => void
}

const HelpIcon = styled.i`
  color: var(--gray-400);
  height: 25px;
  width: 25px;
  position: relative;
  top: 0px;
  left: 1px;
`

export const SearchForm: React.FC<SearchFormProps> = ({
  showPrintBtn,
  setNewQuery,
  formData,
  hasClaimNumber,
  setHasClaimNumber,
  setClaimNumberQuery,
  setClaimMemberQuery,
  setFormData,
  printRef,
  onBeforePrint,
  onAfterPrint
}) => {
  const onSubmit = (e: any) => {
    e.preventDefault()

    // set query to be enabled
    setNewQuery(true)

    const { providerId, claimNumber, memberId, dateOfBirth, billedAmount } =
      formData

    // set query data

    if (hasClaimNumber) {
      const queryData = {
        provider_id: providerId,
        claim_number: claimNumber
      }

      setClaimNumberQuery(queryData)
    } else {
      let queryData

      if (memberId.length === 9) {
        queryData = {
          provider_id: providerId,
          angle_id: 'ANG' + memberId,
          date_of_birth: dateOfBirth,
          billed_amount: Number(billedAmount)
        }
      } else {
        queryData = {
          provider_id: providerId,
          angle_id: 'ANG' + memberId,
          date_of_birth: dateOfBirth,
          billed_amount: Number(billedAmount),
          first_name: formData.firstName,
          last_name: formData.lastName
        }
      }

      setClaimMemberQuery(queryData)
    }
  }

  const setFormType = (hasClaimNumber: boolean) => {
    setHasClaimNumber(hasClaimNumber)
    // disable query (i.e. clear search results) when switching between form types - I'm guessing this is expected behavior but product could say otherwise
    setNewQuery(false)
  }

  return (
    <div>
      <SmallTitle>
        Search for claims via claim ID or information about the member
      </SmallTitle>
      <Form onSubmit={onSubmit}>
        <Form.Group className="pb-3">
          <FormLabel>Servicing NPI or TIN</FormLabel>
          <OverlayTrigger
            placement="right"
            overlay={
              <Tooltip
                data-testid="about-npi-tin-tooltip"
                className='npi-tin-tooltip'
                id="NPI or TIN tooltip"
              >
                <ul style={{ textAlign: 'left' }}>You can provide either of the following:</ul>
                <li style={{ textAlign: 'left' }}>National Provider Identifier (NPI): unique 10 digit number for healthcare providers</li>
                <li style={{ textAlign: 'left' }}>Tax Identification Number (TIN): unique 9 digit number for insurers, TPAs, and employers</li>
              </Tooltip>
            }
          >
            <HelpIcon
              data-testid="about-npi-tin-tooltip-trigger"
              className="icon icon-question-mark fs-12"
            />
          </OverlayTrigger>
          <FormControl
            maxLength={10}
            placeholder="Please enter a valid 10-digit NPI or 9-digit TIN without dashes"
            value={formData.providerId}
            required
            onChange={(e: any) =>
              setFormData((prevState) => ({
                ...prevState,
                providerId: e.target.value
              }))
            }
          ></FormControl>
        </Form.Group>
        <Form.Group className="pb-4">
          <FormLabel>Do you have a claim number?</FormLabel>
          <div>
            <Button
              variant="outline-primary"
              active={hasClaimNumber}
              onClick={() => setFormType(true)}
              style={{ width: 120, marginRight: 10 }}
            >
              Yes
            </Button>
            <Button
              variant="outline-primary"
              active={!hasClaimNumber}
              onClick={() => setFormType(false)}
              style={{ width: 120 }}
            >
              No
            </Button>
          </div>
        </Form.Group>
        {hasClaimNumber ? (
          <ClaimNumberSearch formData={formData} setFormData={setFormData} />
        ) : (
          <MemberSearch formData={formData} setFormData={setFormData} />
        )}
        <div className="py-5">
          <Button className="py-2 mr-3" type="submit">
            Search
          </Button>
          {showPrintBtn && (
            <ReactToPrint
              trigger={() => (
                <Button className="py-2" variant="outline-primary">
                  Print Results
                </Button>
              )}
              content={() => printRef.current}
              onBeforeGetContent={onBeforePrint}
              onAfterPrint={onAfterPrint}
            />
          )}
        </div>
      </Form>
    </div>
  )
}
